import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { IoShareSocialOutline } from 'react-icons/io5';
import { Countdown, Loader, NewsRollDrawer, PageMeta, StateFlagsNav, VoteUpdates } from '../';
import moment from 'moment';
import i18next from 'i18next';
import axios from 'axios';
import 'moment/locale/zh-cn';
import { Gpt } from 'react-gpt-ads';
import zhConvertor from 'zhconvertor';
import { enabledStates } from '../../config/states';
import ErrorImage from '../../img/og-image.jpg';
import ManifestoSection from './ManifestoSection';
import VideoSection from './VideoSection';
import DashboardGrid from './DashboardGrid';
import SwingSeatsSection from './SwingSeatsSection';
import PersonalInterestSection from './PersonalInterestSection';
import PartyArticlesPanel from '../SeatDetail/PartyArticlesPanel';
import SeatInfoPanel from '../SeatDetail/SeatInfoPanel';
import { trackEvent } from '../../utils/analytics';
import { recordSeat } from '../../utils/interestTracker';
import { isBeforeTarget } from '../../utils/electionTime';
import { fetchArticleGallery } from '../../utils/articleGallery';
import './Home.css';

moment().locale('zh-cn');

const SHARE_TITLE = '2026森美兰州选 - 东方网';
const SHARE_TEXT = '获取2026森美兰州选的最新消息';

const truncate = (text = '', max = 50) =>
  text.length > 100 ? `${text.substring(0, max)}...` : text;

const shareCard = (e, data) => {
  e.preventDefault();
  e.stopPropagation();
  const payload = {
    title: data.title || '2026森美兰州选 - 东方网',
    text: data.title || '',
    url: data.url,
  };
  trackEvent('share_click', { url: data.url, title: data.title, location: 'home_card' });
  if (navigator.share) {
    navigator.share(payload).catch(() => {});
  } else if (navigator.clipboard && data.url) {
    navigator.clipboard.writeText(data.url).catch(() => {});
  }
};

const NewsCardImage = ({ src, alt, onError }) => (
  <div className="news-card__media">
    <img src={src} alt={alt} onError={onError} loading="lazy" />
  </div>
);

const NewsCard = ({ data, isPinned, onErrorImg }) => (
  <article className={`news-card ${isPinned ? 'news-card--pinned' : ''}`}>
    <a
      className="news-card__link"
      href={data.url}
      target="_blank"
      rel="noreferrer"
      onClick={() => trackEvent('article_click', { url: data.url, title: data.title, location: 'home' })}
    >
      <NewsCardImage src={data.picurl} alt={data.id} onError={onErrorImg} />
      <div className="news-card__body">
        <div className="news-card__meta">
          <time dateTime={data.date} className="news-card__date">
            {moment(data.date).fromNow()}
          </time>
          {(isPinned || data.pinnews) && (
            <span className="tag tag--hot">{i18next.t('热闻')}</span>
          )}
          <button
            type="button"
            className="news-card__share"
            aria-label="Share"
            onClick={(e) => shareCard(e, data)}
          >
            <IoShareSocialOutline />
          </button>
        </div>
        <h3 className="news-card__title">{data.title}</h3>
        {data.description && (
          <p className="news-card__desc">{truncate(data.description)}</p>
        )}
      </div>
    </a>
  </article>
);

const GalleryCard = ({ data, gallery }) => (
  <article className="news-card news-card--gallery">
    <a
      className="news-card__link"
      href={data.url}
      target="_blank"
      rel="noreferrer"
      onClick={() => trackEvent('article_click', { url: data.url, title: data.title, location: 'home_gallery' })}
    >
      <div className="news-card__body">
        <div className="news-card__meta">
          <time dateTime={data.date} className="news-card__date">
            {moment(data.date).fromNow()}
          </time>
          <span className="tag tag--hot">{i18next.t('图集') || '图集'}</span>
          <button
            type="button"
            className="news-card__share"
            aria-label="Share"
            onClick={(e) => shareCard(e, data)}
          >
            <IoShareSocialOutline />
          </button>
        </div>
        <h3 className="news-card__title">{data.title}</h3>
      </div>
    </a>
    {gallery && gallery.images && (
      <div className="gallery-grid">
        {gallery.images.map((src, idx) => (
          <div key={idx} className="gallery-image-wrapper">
            <img src={src} alt={`${data.id}-${idx}`} loading="lazy" />
          </div>
        ))}
      </div>
    )}
  </article>
);

const Home = () => {
  const states = enabledStates();
  const navigate = useNavigate();

  const [article, setArticle] = useState([]);
  const [gallery, setGallery] = useState({});
  const [pinNews, setPinNews] = useState([]);
  const [videos, setVideos] = useState([]);
  const [load, setLoad] = useState(16);
  const [done, setDone] = useState(false);
  const [focusSeat, setFocusSeat] = useState([]);
  const [tagArticle, setTagArticle] = useState(null);
  // Live seat results (drives the vote-updates section + its seat panel).
  const [seatData, setSeatData] = useState([]);
  const [pastData, setPastData] = useState(null); // null until fetched
  const [selectedSeatName, setSelectedSeatName] = useState(null);

  const replaceErrorImg = (e) => {
    e.target.onerror = null;
    e.target.src = ErrorImage;
  };

  useEffect(() => {
    let cancelled = false;

    const fetchData = async () => {
      // Shared single-flight cache: the notification bar's pins poll resolves
      // from this same request instead of hitting the endpoint separately.
      const data = await fetchArticleGallery();
      if (cancelled) return;
      if (data) {
        setPinNews(data.pins || []);
        setArticle(data.articles || []);
        setGallery(data.gallery || {});
        setVideos(data.videos || []);
        setFocusSeat(data.focusseat || []);
      }
      setDone(true);
    };

    fetchData();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const fetchSeats = () => {
      const lang = localStorage.getItem('lang') || 'zh_CN';
      axios
        .get(`public-api/seatinfo?seat=negerisembilan&code=N&lang=${lang}`)
        .then((res) => {
          if (!cancelled) setSeatData(res.data || []);
        })
        .catch(() => {});
    };
    fetchSeats();
    const interval = setInterval(fetchSeats, 15000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, []);

  // Previous-election seats, fetched once (historical, never changes; the
  // backend caches it for a day). Single shared source for the dashboard's
  // 上届 tab (map fills + chart bars) and the swing-seats past column — the
  // per-card chart/getInfo polls and SwingSeatsSection's own fetches are gone.
  useEffect(() => {
    let cancelled = false;
    const lang = localStorage.getItem('lang') || 'zh_CN';
    axios
      .get(`public-api/seatinfo?seat=negerisembilan&code=N&last_election=true&lang=${lang}`)
      .then((res) => {
        if (!cancelled) setPastData(res.data || []);
      })
      .catch(() => {
        if (!cancelled) setPastData([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // True while any seat's result is still unofficial (is_o !== 1). Suppressed
  // before polling day — nothing is being counted yet, so the flag is noise.
  const resultsUnofficial =
    !isBeforeTarget() &&
    seatData.length > 0 && !seatData.every((s) => Number(s.is_o) === 1);

  const handleVoteUpdateClick = (seatname) => {
    trackEvent('vote_update_click', { seat: `Negerisembilan ${seatname}`, location: 'home' });
    const seatRow = seatData.find((s) => s.seatname === seatname);
    recordSeat({
      slug: 'negerisembilan',
      seatname,
      label: seatRow && seatRow.ednamec ? seatRow.ednamec : seatname,
    });
    setSelectedSeatName(seatname);
  };

  const handleSwingSeatClick = (seatname) => {
    trackEvent('swing_seat_click', { seat: `Negerisembilan ${seatname}`, location: 'home' });
    const seatRow = seatData.find((s) => s.seatname === seatname);
    recordSeat({
      slug: 'negerisembilan',
      seatname,
      label: seatRow && seatRow.ednamec ? seatRow.ednamec : seatname,
    });
    setSelectedSeatName(seatname);
  };

  const onShare = () => {
    trackEvent('share_click', { url: window.location.href, location: 'home_floating' });
    if (navigator.share) {
      navigator.share({
        title: SHARE_TITLE,
        text: SHARE_TEXT,
        url: window.location.href,
      });
    }
  };

  if (!done) return <Loader />;

  return (
    <>
      <PageMeta />
      <div className="home-page">
        <header className="home-hero">
          <div className="home-hero__inner">
            <div className="home-hero__heading">
              <h1 className="home-hero__title">2026 森美兰州选</h1>
            </div>
          </div>
        </header>

        {/*<SeatsOverview />

        <div className="home-ad-slot">
          <Gpt
            adUnit="/2163006/election2023_top"
            name="div-gpt-ad-home-seats-sample"
            size={[[300, 250], [336, 280], 'fluid']}
          />
        </div>*/}

        <Countdown />

        <VoteUpdates data={seatData} onSeatClick={handleVoteUpdateClick} />

        <DashboardGrid
          states={states}
          unofficial={resultsUnofficial}
          seatsBySlug={{ negerisembilan: seatData }}
          pastBySlug={{ negerisembilan: pastData }}
        />

        <PersonalInterestSection onTagClick={setTagArticle} />

        <SwingSeatsSection
          current={seatData}
          past={pastData}
          onSeatClick={handleSwingSeatClick}
          onTagClick={setTagArticle}
        />

        {/*<Link to="/historical" className="historical-cta">
          <span className="historical-cta__label">{i18next.t('历史上的国席成绩')}</span>
          <span className="historical-cta__hint">1955 — 2022</span>
          <span className="historical-cta__arrow" aria-hidden="true">›</span>
        </Link>*/}

        

        <div className="home-ad-slot">
          <Gpt
            adUnit="/2163006/election_head"
            name="div-gpt-ad-1688091342624-0"
            size={['fluid', [300, 250], [336, 280], [1, 1]]}
          />
        </div>

        {/*<ManifestoSection />*/}

        <div className="section-title">{i18next.t('选情')}</div>
        <section className="news-list">
          {pinNews && pinNews.length > 0 && (
            <div className="news-list__pinned">
              {pinNews.map((pin, idx) => (
                <NewsCard
                  key={idx}
                  data={pin}
                  isPinned
                  onErrorImg={replaceErrorImg}
                />
              ))}
            </div>
          )}

          {article.slice(0, load).map((art, index) => (
            <div key={index} className="news-list__row">
              {index === 7 && (
                <Gpt
                  adUnit="/2163006/election_336x280_300x250"
                  name="div-gpt-ad-1599627962119-0"
                  size={[[300, 250], [336, 280], 'fluid']}
                />
              )}
              {index === 14 && (
                <Gpt
                  adUnit="/2163006/election_336x280_300x250_2"
                  name="div-gpt-ad-1599823689403-0"
                  size={[[300, 250], [336, 280]]}
                />
              )}
              {index === 21 && (
                <Gpt
                  adUnit="/2163006/election_300x250_336x280_3"
                  name="div-gpt-ad-1599907131243-0"
                  size={[[300, 250], [336, 280], 'fluid']}
                />
              )}

              {index === 5 && focusSeat && focusSeat.length > 0 && (
                <div className="focus-section">
                  <div className="section-title">{i18next.t('选战热话')}</div>
                  <ul className="focus-section-ul">
                    {focusSeat.map((eachFocus, i) => {
                      const label =
                        localStorage.getItem('lang') !== 'zh_TW'
                          ? zhConvertor.t2s(eachFocus)
                          : eachFocus;
                      return (
                        <li key={i}>
                          <button type="button" onClick={() => setTagArticle(label)}>
                            <div className="each-focus">{label}</div>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}

              

              {index === 2 && <VideoSection videos={videos} />}

              {index === 20 && (
                <div className="apps-wrapper">
                  <div className="section-title">{i18next.t('东方网 APP') || '东方网 APP'}</div>
                  <div className="apps-download">
                    <a
                      href="https://apps.apple.com/us/app/oriental-daily-%E4%B8%9C%E6%96%B9%E6%97%A5%E6%8A%A5-%E9%A9%AC%E6%9D%A5%E8%A5%BF%E4%BA%9A%E7%83%AD%E7%82%B9%E6%96%B0%E9%97%BB/id1550134045"
                      target="_blank"
                      rel="noreferrer"
                    >
                      <img alt="App Store" src="img/appleappstore.png" />
                    </a>
                    <a
                      href="https://play.google.com/store/apps/details?id=com.my.orientaldailynews"
                      target="_blank"
                      rel="noreferrer"
                    >
                      <img alt="Google Play" src="img/googleplay.png" />
                    </a>
                    <a
                      href="https://appgallery.huawei.com/#/app/C103900095"
                      target="_blank"
                      rel="noreferrer"
                    >
                      <img alt="App Gallery" src="img/huaweiappgallery.png" />
                    </a>
                  </div>
                </div>
              )}

              {art.type === 'gallery' ? (
                <GalleryCard data={art} gallery={gallery[art.id]} />
              ) : (
                <NewsCard data={art} onErrorImg={replaceErrorImg} />
              )}
            </div>
          ))}
        </section>
      </div>

      <div className="load-page">
        <button
          type="button"
          className="btn-primary btn-load"
          onClick={() => setLoad((p) => p + 12)}
        >
          更多文章
        </button>
      </div>

      <NewsRollDrawer />
      <StateFlagsNav />
      <SeatInfoPanel
        seat={seatData.find((s) => s.seatname === selectedSeatName) || null}
        seatName={selectedSeatName}
        onClose={() => setSelectedSeatName(null)}
        onViewDetail={() => {
          const seatname = selectedSeatName;
          setSelectedSeatName(null);
          navigate(`/seats/negerisembilan/n#s${seatname}`);
        }}
      />
      <PartyArticlesPanel
        tag={typeof tagArticle === 'object' && tagArticle ? tagArticle.tag : tagArticle}
        fallbackTag={typeof tagArticle === 'object' && tagArticle ? tagArticle.fallbackTag : null}
        location={(typeof tagArticle === 'object' && tagArticle && tagArticle.location) || 'party_panel'}
        onClose={() => setTagArticle(null)}
      />

      {navigator.share && (
        <button
          type="button"
          className="floating-share-button"
          onClick={onShare}
          aria-label="Share"
        />
      )}
    </>
  );
};

export default Home;
